import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown, Avatar, Button, message, Empty, Spin } from 'antd';
import {
  NotificationDropdownWrapper,
  NotificationItem,
  NotificationHeader,
  NotificationContent,
  NotificationActions,
  Badge,
} from './NotificationDropdown.style';
import { BellOutlined, CheckOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import { BASE_URL } from '../../constants/api-urls';
import socketService from '../../services/socketService';
import { useNavigate } from 'react-router-dom';

interface Notification {
  _id: string;
  type: 'invitation_received' | 'invitation_accepted' | 'access_revoked';
  invitationId: string;
  inviterName: string;
  inviteeName: string;
  isRead: boolean;
  createdAt: string;
  expiresAt: string;
}

interface NotificationDropdownProps {
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  visible,
  onVisibleChange,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingRead, setMarkingRead] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Get current user ID
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const userId = userInfo.userId;

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!userId) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(`${BASE_URL}/curator-notifications?userId=${userId}`);

      if (response.data.success) {
        setNotifications(response.data.data);
      } else {
        console.error(
          '🔔 [NotificationDropdown] Failed to fetch notifications:',
          response.data.message
        );
      }
    } catch (error) {
      console.error('🔔 [NotificationDropdown] Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    setMarkingRead(notificationId);

    try {
      const response = await axios.put(`${BASE_URL}/curator-notifications/${notificationId}/read`, {
        userId,
      });

      if (response.data.success) {
        setNotifications(prev =>
          prev.map(notif => (notif._id === notificationId ? { ...notif, isRead: true } : notif))
        );
      } else {
        console.error('🔔 [NotificationDropdown] Failed to mark notification as read');
        message.error('Failed to mark notification as read');
      }
    } catch (error) {
      console.error('🔔 [NotificationDropdown] Error marking notification as read:', error);
      message.error('Failed to mark notification as read');
    } finally {
      setMarkingRead(null);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: string) => {
    setDeleting(notificationId);

    try {
      const response = await axios.delete(
        `${BASE_URL}/curator-notifications/${notificationId}?userId=${userId}`
      );

      if (response.data.success) {
        setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
      } else {
        console.error('🔔 [NotificationDropdown] Failed to delete notification');
        message.error('Failed to delete notification');
      }
    } catch (error) {
      console.error('🔔 [NotificationDropdown] Error deleting notification:', error);
      message.error('Failed to delete notification');
    } finally {
      setDeleting(null);
    }
  };

  // Handle notification click - navigate to Team management tab
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.isRead) {
      markAsRead(notification._id);
    }

    // Navigate to Settings → Team management tab
    window.location.href = '/settings?tab=4'; // Tab 4 is Team management
    onVisibleChange(false);
  };

  // Handle real-time notifications
  const handleNewNotification = useCallback(
    (data: any) => {
      // Add new notification to the list
      const newNotification: Notification = {
        _id: data._id,
        type: data.type,
        invitationId: data.invitationId,
        inviterName: data.inviterName,
        inviteeName: data.inviteeName,
        isRead: false,
        createdAt: data.createdAt,
        expiresAt: data.expiresAt,
      };

      setNotifications(prev => {
        const updated = [newNotification, ...prev];
        return updated;
      });

      // If access was revoked, check if user is on a shared profile and switch back
      if (data.type === 'access_revoked') {
        // Check spotifyUser instead of userInfo to detect shared profile
        const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
        const isOnSharedProfile = spotifyUser.isShared === true;

        if (isOnSharedProfile) {
          // Show toast message
          message.warning(
            `${data.inviterName} revoked your access. Switching back to your profile...`,
            3
          );

          // Reload page to trigger profile refresh
          // The profile switching logic in MainHeader will handle switching back
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [notifications.length]
  );

  // Set up real-time notifications
  useEffect(() => {
    // Add notification listener
    socketService.addNotificationListener(handleNewNotification);

    // Fetch initial notifications
    fetchNotifications();

    // Cleanup
    return () => {
      socketService.removeNotificationListener(handleNewNotification);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Fetch notifications when dropdown becomes visible
  useEffect(() => {
    if (visible) {
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Get notification message
  const getNotificationMessage = (notification: Notification) => {
    switch (notification.type) {
      case 'invitation_received':
        return `${notification.inviterName} invited you to collaborate`;
      case 'invitation_accepted':
        return `${notification.inviteeName} accepted your collaboration invitation`;
      case 'access_revoked':
        return `${notification.inviterName} revoked your access to their profiles`;
      default:
        return 'New notification';
    }
  };

  // Get notification time
  const getNotificationTime = (createdAt: string) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const notificationMenu = (
    <NotificationDropdownWrapper>
      <NotificationHeader>
        <h4>Notifications</h4>
        {unreadCount > 0 && <Badge>{unreadCount}</Badge>}
      </NotificationHeader>

      <NotificationContent>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="small" />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            style={{ margin: '20px 0' }}
          />
        ) : (
          notifications.map(notification => (
            <NotificationItem
              key={notification._id}
              isRead={notification.isRead}
              onClick={() => handleNotificationClick(notification)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <Avatar
                  size={40}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#007AFF', flexShrink: 0, fontSize: '18px' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '14px',
                      lineHeight: '1.5',
                      marginBottom: '6px',
                      color: '#FFFFFF',
                      fontWeight: 500,
                    }}
                  >
                    {getNotificationMessage(notification)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.3' }}>
                    {getNotificationTime(notification.createdAt)}
                  </div>
                </div>
              </div>

              <NotificationActions>
                {!notification.isRead && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={e => {
                      e.stopPropagation();
                      markAsRead(notification._id);
                    }}
                    loading={markingRead === notification._id}
                    style={{ color: '#007AFF', fontSize: '12px', fontWeight: 500 }}
                  >
                    Mark Read
                  </Button>
                )}
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={e => {
                    e.stopPropagation();
                    deleteNotification(notification._id);
                  }}
                  loading={deleting === notification._id}
                  danger
                  style={{ fontSize: '12px', fontWeight: 500 }}
                >
                  Delete
                </Button>
              </NotificationActions>
            </NotificationItem>
          ))
        )}
      </NotificationContent>
    </NotificationDropdownWrapper>
  );

  return (
    <Dropdown
      overlay={notificationMenu}
      trigger={['click']}
      visible={visible}
      onVisibleChange={onVisibleChange}
      overlayClassName="notificationDropdown"
      placement="bottomRight"
    >
      <div style={{ position: 'relative', cursor: 'pointer' }}>
        <BellOutlined style={{ fontSize: '20px', color: '#FFFFFF' }} />
        {unreadCount > 0 && (
          <Badge
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: '#007AFF',
              color: 'white',
              borderRadius: '50%',
              minWidth: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              border: '2px solid #0B0F1B',
              boxShadow: '0 2px 8px rgba(0, 122, 255, 0.4)',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </div>
    </Dropdown>
  );
};

export default NotificationDropdown;
